import { z } from "zod";
import { timeZoneSchema } from "./crm-fields.js";
export const analyticsPeriodSchema = z
    .object({
    from: z.string().datetime({ offset: true }),
    to: z.string().datetime({ offset: true }),
})
    .strict();
export const ANALYTICS_BINS = [
    { minMessages: 1, maxMessages: 5 },
    { minMessages: 6, maxMessages: 10 },
    { minMessages: 11, maxMessages: 20 },
    { minMessages: 21, maxMessages: 50 },
    { minMessages: 51, maxMessages: 100 },
    { minMessages: 101, maxMessages: null },
];
const count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const percentile = z
    .number()
    .finite()
    .min(1)
    .max(Number.MAX_SAFE_INTEGER)
    .nullable();
export const analyticsBinSchema = z
    .object({
    minMessages: count.min(1),
    maxMessages: count.min(1).nullable(),
    leads: count,
})
    .strict();
export const analyticsMetricsSchema = z
    .object({
    sampleSize: count,
    // Optional for immutable reports created before message totals were added.
    messages: z.object({ client: count, agent: count, total: count, unconfirmedAgent: count }).strict().optional(),
    length: z
        .object({
        p50: percentile,
        p75: percentile,
        p95: percentile,
        bins: z
            .array(analyticsBinSchema)
            .length(6)
            .refine((bins) => bins.every((b, i) => b.minMessages === ANALYTICS_BINS[i].minMessages &&
            b.maxMessages === ANALYTICS_BINS[i].maxMessages)),
    })
        .strict(),
    escalations: count,
    qualifiedLeads: count,
    goalAchievedLeads: count,
})
    .strict();
export const analyticsCoverageSchema = z
    .object({
    availableFrom: z.string().datetime().nullable(),
    complete: z.boolean(),
    warnings: z.array(z.enum([
        "COLLECTION_NOT_STARTED",
        "COLLECTION_STARTED_IN_PERIOD",
        "PREVIOUS_PERIOD_PARTIAL",
        "LATE_CONFIRMATIONS_POSSIBLE",
        "APPROXIMATE_EVENT_TIME",
    ])),
})
    .strict();
export const agentAnalyticsSchema = z
    .object({
    schemaVersion: z.literal(1),
    timezone: timeZoneSchema,
    formulaVersion: z.literal("messages-v1"),
    generatedAt: z.string().datetime(),
    period: analyticsPeriodSchema,
    previousPeriod: analyticsPeriodSchema,
    current: analyticsMetricsSchema,
    previous: analyticsMetricsSchema,
    coverage: z
        .object({
        current: analyticsCoverageSchema,
        previous: analyticsCoverageSchema,
    })
        .strict(),
})
    .strict();
// Find the first instant of a calendar date in the account zone. Date-based
// binary search also handles midnight DST gaps/overlaps; no browser-zone parsing.
function localDateStart(date, timeZone) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date))
        return null;
    const utc = Date.parse(`${date}T00:00:00Z`);
    if (!Number.isFinite(utc) ||
        new Date(utc).toISOString().slice(0, 10) !== date)
        return null;
    const formatter = new Intl.DateTimeFormat("en", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    });
    const localDate = (instant) => {
        const parts = formatter.formatToParts(instant);
        const part = (type) => parts.find((p) => p.type === type).value;
        return `${part("year")}-${part("month")}-${part("day")}`;
    };
    let lo = utc - 2 * 86400000, hi = utc + 2 * 86400000;
    while (lo < hi) {
        const mid = Math.floor((lo + hi) / 2);
        if (localDate(mid) < date)
            lo = mid + 1;
        else
            hi = mid;
    }
    return new Date(lo);
}
/** Inclusive local dates -> exact UTC [from,to), null for invalid input/zone. */
export function analyticsCalendarPeriod(fromDate, throughDate, timeZone) {
    if (!timeZoneSchema.safeParse(timeZone).success || fromDate > throughDate)
        return null;
    const from = localDateStart(fromDate, timeZone), through = localDateStart(throughDate, timeZone);
    if (!from || !through)
        return null;
    const next = new Date(`${throughDate}T00:00:00Z`);
    next.setUTCDate(next.getUTCDate() + 1);
    const to = localDateStart(next.toISOString().slice(0, 10), timeZone);
    if (!to || to <= from)
        return null;
    return { from: from.toISOString(), to: to.toISOString() };
}
//# sourceMappingURL=agent-analytics.js.map